import { useEffect, useRef, useState } from "react";
import { ArrowDownToLine, Pause, Play, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { openSse } from "@/lib/api";
import { cn } from "@/lib/utils";

interface LogLine {
  n: number;
  stream: "stdout" | "stderr";
  text: string;
}

/**
 * BuildLogs — SSE-backed live tail of a deployment's build_logs table.
 * Auto-scrolls to bottom while paused === false; supports pause + clear +
 * download to .log. ANSI-colour-aware via a tiny stripper (we render plain
 * text; colour highlighting would require an ANSI parser, deferred).
 */
export function BuildLogs({ deploymentId }: { deploymentId: string }) {
  const [lines, setLines] = useState<LogLine[]>([]);
  const [status, setStatus] = useState<"connecting" | "open" | "closed">("connecting");
  const [paused, setPaused] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Reset stale log lines from a previous deployment before connecting,
    // otherwise navigating to a freshly-enqueued redeploy shows the OLD
    // build's logs until the new SSE stream catches up.
    setLines([]);
    const es = openSse(`/deployments/${deploymentId}/logs`);
    setStatus("connecting");
    es.addEventListener("open", () => setStatus("open"));
    es.addEventListener("line", (e) => {
      try {
        const data = JSON.parse((e as MessageEvent).data) as LogLine;
        setLines((prev) => prev.concat(data));
      } catch { /* ignore */ }
    });
    es.addEventListener("status", () => { /* surfaced by parent */ });
    es.addEventListener("end", () => { setStatus("closed"); es.close(); });
    es.addEventListener("error", () => setStatus("closed"));
    return () => es.close();
  }, [deploymentId]);

  useEffect(() => {
    if (paused) return;
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [lines, paused]);

  function download() {
    const text = lines.map((l) => l.text).join("\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `build-${deploymentId.slice(0, 8)}.log`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="of-card overflow-hidden">
      <div className="of-card-header flex-wrap gap-y-2">
        <div className="flex items-center gap-2">
          <span className={cn(
            "inline-flex items-center gap-1.5 text-xs font-medium",
            status === "open" ? "text-success" : status === "closed" ? "text-muted-foreground" : "text-attention",
          )}>
            <span className={cn(
              "h-1.5 w-1.5 rounded-full",
              status === "open" ? "bg-success animate-pulse-dot" : status === "closed" ? "bg-muted-foreground" : "bg-attention",
            )} />
            {status === "open" ? "Live" : status === "closed" ? "Complete" : "Connecting"}
          </span>
          <span className="text-2xs text-subtle-foreground font-mono">{lines.length} lines</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => setPaused((p) => !p)} title={paused ? "Resume autoscroll" : "Pause autoscroll"}>
            {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={download} title="Download .log">
            <ArrowDownToLine className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setLines([])} title="Clear view">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <div className="of-mono-surface max-h-[60vh] overflow-auto">
        <pre className="font-mono text-xs leading-5 px-0 py-3">
          {lines.length === 0 && status !== "closed" ? (
            <div className="px-4 text-subtle-foreground italic">Waiting for build output…</div>
          ) : null}
          {lines.map((l) => (
            <div
              key={l.n}
              className={cn(
                "grid grid-cols-[3.5rem_1fr] gap-3 px-4 py-px hover:bg-subtle/40 transition-colors",
                l.stream === "stderr" && "text-danger",
              )}
            >
              <span className="text-right text-subtle-foreground select-none tabular-nums">{l.n}</span>
              <span className="whitespace-pre-wrap break-words">{l.text}</span>
            </div>
          ))}
          <div ref={bottomRef} />
        </pre>
      </div>
    </div>
  );
}
